import { useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  updateConventionStore,
  conventionSlice,
} from "../../../store/conventionSlice";
import {
  listenToConventionUpdates,
  listenToProductUpdates,
  listenToPublishConvention,
  listenToUnpublishConvention,
} from "../../../sockets";

/**
 * @desc A custom hook for listening to sockets.
 */
export const useListenSockets = ({ brand, cname, isLive, products }) => {
  // CONSTANTS ----------------------------------------------------------------

  const dispatch = useDispatch();

  // METHODS ------------------------------------------------------------------

  /**
   * @desc Callback to execute on convention update.
   */
  const updateConventionCallback = useCallback(
    (res) => {
      dispatch(updateConventionStore(res));
    },
    [dispatch]
  );

  /**
   * @desc Callback to execute on product update.
   */
  const updateProductCallback = useCallback(
    (res) => {
      const psku = res.sku;
      const replaceIndex = products.findIndex(({ sku }) => sku === psku);
      if (replaceIndex > -1) {
        const clone = [...products];
        clone[replaceIndex] = res;
        dispatch(updateConventionStore({ products: clone }));
      }
    },
    [products, dispatch]
  );

  /**
   * @desc Callback to execute on publish convention.
   */
  const publishConventionCallback = useCallback(
    (res) => {
      dispatch(conventionSlice.actions.updateConventionStore(res));
    },
    [dispatch]
  );

  /**
   * @desc Callback to execute on unpublish convention.
   */
  const unpublishConventionCallback = useCallback(() => {
    dispatch(conventionSlice.actions.reset());
  }, [dispatch]);

  // SIDE EFFECTS -------------------------------------------------------------

  /**
   * @desc Only once:
   *  - Create socket to listen for convention updates
   */
  useEffect(() => {
    if (isLive) {
      listenToConventionUpdates(updateConventionCallback, brand);
    }
  }, [brand, updateConventionCallback, isLive]);

  /**
   * @desc Only once:
   *  - Create socket to listen for product updates
   */
  useEffect(() => {
    if (isLive) {
      listenToProductUpdates(updateProductCallback, cname);
    }
  }, [cname, updateProductCallback, isLive]);

  /**
   * @desc Only once:
   *  - Create socket to listen for published convention
   */
  useEffect(() => {
    listenToPublishConvention(publishConventionCallback, brand);
  }, [brand, publishConventionCallback]);

  /**
   * @desc Only once:
   *  - Create socket to listen for unpublished convention
   */
  useEffect(() => {
    if (isLive) {
      listenToUnpublishConvention(unpublishConventionCallback, brand);
    }
  }, [brand, unpublishConventionCallback, isLive]);
};
